import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AuthContext } from "../auth-context";
import { RequestContext } from "../request-context";
import { requestGet, requireResult } from "../utils/network";
import { checkPasswordStrength } from "../utils/security";
import ActionButton from "../widget/action-button";
import ButtonsGroup from "../widget/buttons-group";
import { Modes } from "../widget/io-abstract";
import IOBoolean from "../widget/io-boolean";
import IOPanel from "../widget/io-panel";
import IOString from "../widget/io-string";
import type { User } from "./_objects-users";
import "./page-password.css";

export default function PagePassword(props: {
  profile?: boolean;
}): React.ReactNode {
  const params = useParams();
  const [searchParams, _] = useSearchParams();
  const requestContext = useContext(RequestContext)!;
  const navigate = useNavigate();
  const authenticationContext = useContext(AuthContext)!;
  const [user, setUser] = useState<User>({} as User);
  const [currentPassword, setCurrentPassword] = useState("");

  const userId = props.profile
    ? authenticationContext.authenticatedData?.user.id
    : params["id"];
  const redirectTo: string = searchParams.get("redirect")
    ? searchParams.get("redirect")!
    : props.profile
      ? "/home"
      : `/user/${userId}`;

  useEffect(() => {
    if (userId) {
      requestContext
        .fetchJSONAndMessage<User>(
          `Loading #${userId}`,
          requestGet(`/api/user/${userId}`)
        )
        .then(requireResult)
        .then((user) => setUser(user))
        .catch(() => {});
    }
  }, [userId]);

  const goBack = () => navigate(redirectTo);

  if (!user.id) {
    return <></>;
  }

  return (
    <>
      <ButtonsGroup>
        <ActionButton onOk={goBack} action="Go back to user" />
      </ButtonsGroup>
      <IOPanel<{ id: string; password?: string }>
        apiRootUrl={`/api/user/${userId}`}
        type="user"
        edit
        file={{ id: "password" }}
        isProtected={true}
        restrictedForWrite=""
        onCreated={() => {}}
        onDeleted={() => {}}
        onUpdated={() => goBack()}
        onEdit={(edit) => {
          if (!edit) {
            // setCurrentPassword("");
            goBack();
          }
        }}
        header={<span>Change password for {user.name}</span>}
      >
        <div className="alert alert-info">
          If you want to disable a password, leave the field empty.
        </div>
        {currentPassword == "" && (
          <div className="alert alert-warning">
            This user has no password, so s.he won't be able to log in
          </div>
        )}
        <IOBoolean
          name="hasAPassword"
          value={user.has_password}
          mode={Modes.output}
        />
        <IOString
          name="password"
          label="Override current password"
          value=""
          onChange={(value) => setCurrentPassword(value)}
          validation={(value) => {
            if (!value) {
              return "";
            }

            if (value?.startsWith("$")) {
              return "The password can NOT start with a dollar sign ($).";
            }

            return checkPasswordStrength(value ?? "").valid
              ? ""
              : "Password must be stronger";
          }}
          inputHelp={
            <div id="password-help" style={{ display: "flexbox" }}>
              <span className="badge text-bg-info">
                {checkPasswordStrength(currentPassword).level}
              </span>
              <span>
                A good password contains:
                <ul>
                  <li>8 characters minimum</li>
                  <li>upper and lower case letters</li>
                  <li>numbers</li>
                  <li>special characters</li>
                </ul>
              </span>
            </div>
          }
        />
        <br />
      </IOPanel>
    </>
  );
}
